import { useHistory } from "react-router-dom";
import { useActiveLang } from "../../domains/app/hooks/useActiveLang";
import { usePageLinks } from "../../domains/app/hooks/usePageLinks";
import { usePlaylistPanelContent } from "../../domains/playlist/hooks/usePlaylistPanelContent";
import { useSaveVideo } from "../../domains/user/hooks/useSaveVideo";
import { useShareVideo } from "../../domains/user/hooks/useShareVideo";
import { useCurrentUser } from "../../domains/user/hooks/useCurrentUser";
import type { Api } from "../../services/api";
import { thumbnailUrl } from "../../../../api/src/shared/domain/video-url";
import { AnalyticsItemList } from "../../tools/analytics/analytics.types";
import { gaEventSelectItemVideo } from "../../tools/analytics/domains/video.analytics";
import PlaylistPanel from "../app/PlaylistPanel";
import { useCongressAttribution } from "../../domains/congress/congressAttribution";
import { ContentPublicationCard } from "../ui/ContentPublicationCard";
import type { PublicationCardRecommendation } from "../ui/PublicationCard";
import type { ForYouFeedItem } from "../../domains/forYou/hooks/useGetForYouRecommendedContentQuery";
import { journalSource } from "../ui/SourceLabel";

type VideoListItem = Extract<
  Api["get"]["/webapp/public/content"]["output"]["body"]["docs"][number],
  { content_format: "Video" }
>;
type FeedVideoItem = Extract<ForYouFeedItem, { content_format: "Video" }>;

type VideoCardData = VideoListItem | FeedVideoItem;

function formatDate(value: Date | string | null, locale: string): string {
  if (value === null) return "";
  const date = value instanceof Date ? value : new Date(value);
  return date.toLocaleDateString(locale, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function formatDuration(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}m ${s.toString().padStart(2, "0")}s`;
}

export default function VideoCard({
  roomId,
  video,
  analyticsListName,
  recommendation,
  onClick,
  className,
}: {
  roomId: string | null;
  video: VideoCardData;
  analyticsListName: AnalyticsItemList;
  recommendation: PublicationCardRecommendation | null;
  onClick: (() => void) | null;
  className: string | null;
}) {
  const history = useHistory();
  const { activeLang } = useActiveLang();
  const { videoArticleLink, videoStoryLink } = usePageLinks(roomId);
  const { currentUser } = useCurrentUser();
  const playlistPanel = usePlaylistPanelContent();
  const congressAttribution = useCongressAttribution(analyticsListName);
  const { isSaved, toggleSaveToPlaylists } = useSaveVideo(
    video,
    analyticsListName
  );
  const { triggerShare } = useShareVideo(analyticsListName);

  const source = journalSource(video.journal);
  const thumb = thumbnailUrl(video);
  const detail =
    video.duration === null ? null : formatDuration(video.duration);

  return (
    <>
      <ContentPublicationCard
        contentType="video"
        title={video.title}
        source={source}
        date={formatDate(video.publication_date, activeLang)}
        meta="Video"
        metaVariant="lemon"
        detail={detail}
        imageUrl={thumb}
        recommendation={recommendation}
        isBookmarked={isSaved}
        onBookmark={
          currentUser === null
            ? null
            : () => {
                void playlistPanel.open(video.id);
              }
        }
        onShare={
          currentUser === null
            ? null
            : () => {
                void triggerShare(video, currentUser, roomId);
              }
        }
        onClick={() => {
          if (onClick !== null) onClick();
          gaEventSelectItemVideo({
            video,
            listName: analyticsListName,
          });
          const videoFormat = video.videoFormat;
          const path =
            videoFormat === "article"
              ? videoArticleLink(video)
              : videoStoryLink(video);
          // Attribute the open to a congress space when this card is
          // drawing one. Gated on `analyticsListName`, NOT on tree position:
          // this component renders its own PlaylistPanel below, whose cards
          // would otherwise inherit the congress from the provider. The
          // destination page reads this back and attaches it to the
          // `view_item` it already fires.
          history.push({ pathname: path, state: { congressAttribution } });
        }}
        className={className === null ? undefined : className}
      />
      {playlistPanel.state.status === "ready" && (
        <PlaylistPanel
          isVisible
          contentToAdd={playlistPanel.state.content}
          onClose={playlistPanel.close}
          onSave={async ({
            add,
            remove,
          }: {
            add: string[];
            remove: string[];
          }) => {
            await toggleSaveToPlaylists(video, { add, remove });
          }}
        />
      )}
    </>
  );
}
